/**
 * The order page (`/shop/orders/:orderId`, studio-shop D8, the shop-orders and
 * shop-checkout specs), also the thank-you page right after paying. Built once
 * for the website (the escape hatch in shared-client-screens.md).
 *
 * The read token (`k`) leaves the address bar as soon as it is read and stays
 * in this tab; the signed-in owner needs none. A made-up or another studio's
 * order is the studio's not-found page. Right after paying, the page confirms
 * the payment, then offers Telegram: signing in links the order with this
 * browser's claim, and the page says honestly where the updates will come.
 */
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { shownOptionLabel } from '@telify/shared/shop/line-label';
import type { BuyerOrderDto, LinkedOrderDto } from '@telify/shared/shop/site-dtos';
import { useQueryClient } from '@tanstack/react-query';

import { useTelegramSignIn, useWebSession } from '../../api/session';
import { useSiteStudio } from '../../api/site';
import { arrivalDates, linkShopOrder, orderClaim, shopKeys, shopMoney, shopPrice, takeReadToken, useShopOrder } from '../../api/shop';
import { partsOf, pieceRoot } from '../../contract/runtime';
import { contract } from '../../pieces/shop-order/contract';
import { readPluginOption } from '../../site';
import shared from '../../styles/shared.module.css';
import { Icon, type IconName } from '../../ui/Icon';
import { PrintPicture } from '../../ui/PrintPicture';
import { usePageTitle } from '../../ui/usePageTitle';
import { NotFoundPage } from '../NotFoundPage';
import css from './ShopOrder.module.css';
import frame from './ShopPages.module.css';

const PAID = new Set(['paid', 'partly_refunded', 'refunded']);
/** The page's published parts (`shop-order`), so a custom site styles it as it styles the pieces. */
const part = partsOf(contract);

/** The shop plug-in's signInOffer option, as its declaration shapes it. */
interface SignInOffer {
  readonly heading: string;
  readonly lead: string;
  readonly benefits: readonly { readonly title: string; readonly line: string; readonly icon?: keyof typeof OFFER_ICONS }[];
}
/** The option's icon names, as the website's icons call them. */
const OFFER_ICONS = { truck: 'truck', spark: 'spark', calendar: 'cal', chat: 'msg', fast: 'fast', list: 'list' } as const satisfies Record<string, IconName>;
const day = (iso: string) => new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(new Date(iso));
/** "Visa ending 4242", the brand capitalized as bookings show it. */
const cardName = (card: NonNullable<BuyerOrderDto['card']>) => `${card.brand.charAt(0).toUpperCase()}${card.brand.slice(1)} ending ${card.last4}`;

/** D2: a package's estimate ("Nov 2 to 6"), while it is still to arrive; null when its service gave none. */
function arrivesOf(pkg: BuyerOrderDto['parts'][number]): string | null {
  if (pkg.status === 'cancelled' || pkg.status === 'returned' || !pkg.arrivesFrom || !pkg.arrivesTo) return null;
  return arrivalDates(pkg.arrivesFrom, pkg.arrivesTo);
}

/** One package's progress: Paid, In production, Shipped; or where it ended. */
function PartSteps({ pkg, paidAt }: { pkg: BuyerOrderDto['parts'][number]; paidAt: string | null }) {
  if (pkg.status === 'cancelled' || pkg.status === 'returned') {
    const when = pkg.cancelledAt ? ` on ${day(pkg.cancelledAt)}` : '';
    return <p className={css.ended}>{pkg.status === 'cancelled' ? `Cancelled${when}` : 'Returned'}</p>;
  }
  const shipped = pkg.status === 'shipped';
  const steps = [
    { label: 'Paid', when: paidAt ? day(paidAt) : '', state: css.done },
    shipped
      ? { label: 'In production', when: pkg.placedAt ? day(pkg.placedAt) : '', state: css.done }
      : { label: 'In production', when: pkg.status === 'in_production' ? 'In progress' : 'Starting soon', state: css.now },
    { label: 'Shipped', when: pkg.shippedAt ? day(pkg.shippedAt) : '', state: shipped ? css.done : '' },
  ];
  return (
    <ol className={css.steps}>
      {steps.map((step) => (
        <li key={step.label} className={step.state}>
          <i />
          <b>{step.label}</b>
          <small>{step.when}</small>
        </li>
      ))}
    </ol>
  );
}

function Items({ order }: { order: BuyerOrderDto }) {
  return (
    <div className={css.items}>
      {order.lines.map((line) => (
        <div key={line.optionId} className={css.item} {...part('item')}>
          <span className={css.thumb}>
            {line.imageUrl && <PrintPicture url={line.imageUrl} alt="" size={line.size} canvas={line.canvas} />}
            {line.quantity > 1 && <span className={css.q}>{line.quantity}</span>}
          </span>
          <div>
            <div className={css.itemName} {...part('itemName')}>
              {line.productTitle}
            </div>
            {shownOptionLabel(line.productTitle, line.optionLabel) && <div className={css.sub}>{line.optionLabel}</div>}
          </div>
          <div>{shopPrice(line.lineTotal, order.currency)}</div>
        </div>
      ))}
    </div>
  );
}

function shipToLine(order: BuyerOrderDto): string {
  const a = order.shipTo;
  return `${a.line1}${a.line2 ? `, ${a.line2}` : ''}, ${a.city}${a.state ? `, ${a.state}` : ''} ${a.postalCode}${a.country !== 'US' ? `, ${a.country}` : ''}`;
}

export function ShopOrderPage() {
  const { orderId = '' } = useParams<{ orderId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const thanks = Boolean((location.state as { thanks?: boolean } | null)?.thanks);
  const readToken = useMemo(() => takeReadToken(orderId), [orderId]);
  const claim = useMemo(() => orderClaim(orderId), [orderId]);
  const read = useShopOrder(orderId, readToken, { confirming: thanks });
  const order = read.data;
  const session = useWebSession();
  const studio = useSiteStudio().data;
  const telegram = useTelegramSignIn(session.status === 'signedOut' && Boolean(claim));
  const [linked, setLinked] = useState<LinkedOrderDto | null>(null);
  const [linkProblem, setLinkProblem] = useState<string | null>(null);
  const [notNow, setNotNow] = useState(false);
  usePageTitle(order ? `Order ${order.number}` : undefined);

  // D8: signed in with this browser's claim, the order becomes the buyer's; where its updates go is decided then.
  const canLink = Boolean(order && claim && !order.linked && !linked && session.status === 'signedIn');
  useEffect(() => {
    if (!canLink || !claim) return;
    let live = true;
    linkShopOrder(orderId, claim).then(
      (result) => {
        if (!live) return;
        setLinked(result);
        void queryClient.invalidateQueries({ queryKey: shopKeys.order(orderId) });
      },
      (error: Error) => live && setLinkProblem(error.message),
    );
    return () => {
      live = false;
    };
  }, [canLink, claim, orderId, queryClient]);

  if (read.isError) return <NotFoundPage />;
  if (!order) return null;

  const paid = PAID.has(order.payment);
  const first = (order.buyerName ?? order.shipTo.name).split(' ')[0] || null;
  const total = shopMoney(order.total, order.currency);
  const chatHref = studio?.bookingsBotUsername ? `https://t.me/${studio.bookingsBotUsername}` : null;
  const isLinked = Boolean(linked) || order.linked;
  const byTelegram = linked ? linked.updatesByTelegram : order.updatesByTelegram;
  // The email promise holds only while a package has yet to ship.
  const waitingToShip = order.parts.some((pkg) => pkg.status === 'preparing' || pkg.status === 'in_production');
  // D2: each package on its way, with its service's estimate from checkout.
  const arriving = order.parts.flatMap((pkg, index) => {
    const dates = arrivesOf(pkg);
    return dates ? [{ index, dates }] : [];
  });
  const offerTelegram = Boolean(claim) && telegram.oneTapReady;
  // D8: the offer's words are the shop plug-in's signInOffer, this page's version of its site's own or the general ones.
  const offer = readPluginOption('shop', 'signInOffer') as unknown as SignInOffer;
  const continueWithTelegram = (
    <button type="button" className={`${shared.btn} ${shared.btnPrimary}`} onClick={telegram.signInOneTap} {...part('continueTelegram')}>
      <Icon name="tg" />
      Continue with Telegram
    </button>
  );
  const signInProblem = (linkProblem || telegram.state === 'error') && (
    <p className={css.problem} role="alert">
      {linkProblem ?? 'Telegram sign-in did not finish. Please try again.'}
    </p>
  );

  const updates = isLinked ? (
    <div className={css.updates} {...part('updates')}>
      <h2>{byTelegram ? 'Updates come to you in Telegram' : 'Updates keep coming by email'}</h2>
      <p className={css.note}>
        {byTelegram
          ? 'This order is saved to your account, and your address is kept for next time.'
          : `This order is saved to your account, and its updates go to ${order.email}.`}
      </p>
      {chatHref && (
        <a
          className={`${shared.btn} ${byTelegram ? shared.btnPrimary : shared.btnOutline}`}
          href={chatHref}
          target="_blank"
          rel="noopener noreferrer"
          {...part(byTelegram ? 'openTelegram' : 'openChat')}
        >
          <Icon name="tg" />
          {byTelegram ? 'Open in Telegram' : 'Open chat'}
        </a>
      )}
    </div>
  ) : offerTelegram && thanks && !notNow ? (
    <div className={css.offer} {...part('offer')}>
      <div>
        <h2>{offer.heading}</h2>
        <p className={css.lead}>{offer.lead}</p>
        <div className={css.acts}>
          {continueWithTelegram}
          <button type="button" className={css.textlink} onClick={() => setNotNow(true)} {...part('notNow')}>
            Not now
          </button>
        </div>
        {signInProblem}
      </div>
      <ul className={css.benefits}>
        {offer.benefits.map((benefit, i) => (
          <li key={i} {...part('benefit')}>
            <span>{benefit.icon && <Icon name={OFFER_ICONS[benefit.icon]} small />}</span>
            <span>
              <b>{benefit.title}</b>
              <small>{benefit.line}</small>
            </span>
          </li>
        ))}
      </ul>
    </div>
  ) : offerTelegram && !thanks ? (
    // The mockup's order page: the email promise, and Telegram in its place.
    <div className={css.updates} {...part('updates')}>
      <h2>Get these as messages</h2>
      <p className={css.note}>
        {waitingToShip
          ? `You’ll get an email at ${order.email} when it ships, with tracking. Sign in with Telegram and each step arrives as a message instead.`
          : 'Sign in with Telegram and this order’s updates arrive as messages.'}
      </p>
      {continueWithTelegram}
      {signInProblem}
    </div>
  ) : waitingToShip ? (
    <div className={css.updates} {...part('updates')}>
      <p className={css.note}>{`You’ll get an email at ${order.email} when it ships, with tracking.`}</p>
    </div>
  ) : null;

  if (thanks) {
    return (
      <div className={`${shared.wrap} ${frame.flow}`} {...pieceRoot(contract)} {...part('page')}>
        <div className={css.ty}>
          <div className={css.tyTop}>
            <span className={css.tyMark} aria-hidden="true">
              <Icon name="check" />
            </span>
            <h1 {...part('title')}>{first ? `Thank you, ${first}.` : 'Thank you.'}</h1>
            {order.paidLines.map((line) => (
              <p key={line} {...part('paidLine')}>
                {line}
              </p>
            ))}
            <p role="status">{paid ? 'Your order is in.' : 'We’re confirming your payment…'}</p>
            <p className={css.meta}>{`Order ${order.number} · receipt sent to ${order.email}`}</p>
          </div>
          {paid && updates}
          <div className={`${css.card} ${css.tyOrder}`}>
            <Items order={order} />
            <dl>
              {arriving.length > 0 && (
                <>
                  <dt>Arrives</dt>
                  {arriving.map(({ index, dates }) => (
                    <dd key={index} {...part('arrives')}>
                      {order.parts.length > 1 ? `Package ${index + 1} of ${order.parts.length}: ${dates}` : dates}
                    </dd>
                  ))}
                </>
              )}
              <dt>Ship to</dt>
              <dd {...part('shipTo')}>
                {order.shipTo.name}
                <br />
                {shipToLine(order)}
              </dd>
              <dt>{paid ? 'Paid' : 'Total'}</dt>
              <dd>{paid && order.card ? `${total} · ${cardName(order.card)}` : total}</dd>
            </dl>
          </div>
          <div className={css.center}>
            <button
              type="button"
              className={`${shared.btn} ${shared.btnOutline}`}
              onClick={() => navigate(location.pathname, { replace: true, state: null })}
              {...part('viewOrder')}
            >
              View your order
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${shared.wrap} ${frame.flow}`} {...pieceRoot(contract)} {...part('page')}>
      <div className={css.head}>
        <h1 {...part('title')}>{`Order ${order.number}`}</h1>
        <p className={css.note}>{`Placed ${day(order.createdAt)} · ${total} · receipt sent to ${order.email}`}</p>
      </div>
      <div className={css.od}>
        <div className={css.card}>
          {!paid ? (
            <p className={css.ended} role="status">
              {order.payment === 'abandoned' ? 'This order was never paid.' : 'We’re confirming your payment…'}
            </p>
          ) : (
            order.parts.map((pkg, i) => (
              <section
                key={i}
                className={css.part}
                aria-label={order.parts.length > 1 ? `Package ${i + 1} of ${order.parts.length}` : undefined}
                {...part('package')}
              >
                {order.parts.length > 1 && <h2 className={css.partTitle}>{`Package ${i + 1} of ${order.parts.length}`}</h2>}
                <PartSteps pkg={pkg} paidAt={order.paidAt} />
                {pkg.shipments.length > 0 ? (
                  pkg.shipments.map((s, j) => (
                    <div key={j} className={css.track} {...part('tracking')}>
                      <div>
                        <p>{s.carrier ?? 'Shipped'}</p>
                        {s.trackingNumber && <p className={css.sub}>{`Tracking ${s.trackingNumber}`}</p>}
                        {arrivesOf(pkg) && <p className={css.sub} {...part('arrives')}>{`Arrives ${arrivesOf(pkg)}`}</p>}
                      </div>
                      {s.trackingUrl && (
                        <a
                          className={`${shared.btn} ${shared.btnOutline} ${css.sm}`}
                          href={s.trackingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          {...part('trackPackage')}
                        >
                          Track your package
                        </a>
                      )}
                    </div>
                  ))
                ) : pkg.status === 'preparing' || pkg.status === 'in_production' ? (
                  <div className={css.track} {...part('tracking')}>
                    <div>
                      <p>Being made to order</p>
                      <p className={css.sub}>Tracking appears here when it ships</p>
                      {arrivesOf(pkg) && <p className={css.sub} {...part('arrives')}>{`Arrives ${arrivesOf(pkg)}`}</p>}
                    </div>
                  </div>
                ) : null}
              </section>
            ))
          )}
          <div className={css.spaced}>
            <Items order={order} />
          </div>
          <div className={css.totals} {...part('totals')}>
            <div className={css.line}>
              <span className={css.sub}>Subtotal</span>
              <span>{shopMoney(order.subtotal, order.currency)}</span>
            </div>
            <div className={css.line}>
              <span className={css.sub}>Shipping</span>
              <span>{order.shipping === '0.00' ? 'Free' : shopMoney(order.shipping, order.currency)}</span>
            </div>
            {order.tax !== '0.00' && (
              <div className={css.line}>
                <span className={css.sub}>Sales tax</span>
                <span>{shopMoney(order.tax, order.currency)}</span>
              </div>
            )}
            <div className={`${css.line} ${css.big}`}>
              <span>Total</span>
              <span>{total}</span>
            </div>
            {order.refunds.map((r, j) => (
              <div key={j} className={css.line}>
                <span className={css.sub}>{`Refunded ${day(r.at)}`}</span>
                <span>{shopMoney(r.amount, order.currency)}</span>
              </div>
            ))}
          </div>
          <dl className={css.shipTo} {...part('shipTo')}>
            <dt className={css.sub}>Ship to</dt>
            <dd>{`${order.shipTo.name}, ${shipToLine(order)}`}</dd>
          </dl>
        </div>
        <aside className={css.side}>{paid && updates}</aside>
      </div>
    </div>
  );
}
